import { ReactElement, Suspense, useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Col,
    message,
    Popover,
    Row,
    Tabs,
    Tooltip,
} from 'antd';
import CcxComponentProps from '../../core/CcxComponent';
import styles from './Projects.module.less';
import { Link, useHistory, useParams } from 'react-router-dom';
import LazyLoader from '../LazyLoader';
import DataStores from './dataStores/DataStores';
import Vpcs from './vpcs/Vpcs';
import CreateVpcButton from '../vpc/CreateVpcButton';
import { vpcDisabled } from '../../core/CcxEnv';
import { userReachedDatastoreLimit } from '../layout/AppCreateButton';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import useCmonStatus from '../../core/hooks/useCmonStatus';
import DatastoreFilterPopover, {
    OptionsInterface,
} from './DatastoreFilterPopover';
import { FilterOutlined } from '@severalnines/frontend_hub/libs/icons';
import { clearIntervals } from '../../slices/jobs.slice';
import Deployment from '../../types/Deployment';
import DbParametersTable from '../dbParameters/DbParametersTable';
import usePiwikPro from '../../core/hooks/usePiwikPro';
import { LANDING_PAGE } from '../../core/PiwikConstants';
import useInvites from '../../core/hooks/useInvites';
import { InviteInterface } from '../../types/Invite';
import InviteMemberService from '../../services/InvitesService';
import { CAN_ADD_DATABASE, CAN_ADD_VPC } from '../../core/PermissionRoles';
import { userCheck } from '../../slices/user.slice';
import Can from '../../core/providers/Can';
import { setInitialState } from '../../slices/restore.slice';

interface UrlProps {
    projectUuid: string;
    activeTab: string;
}

interface Props extends CcxComponentProps {}

interface FilterOptionsInterface {
    datastoreTypes: OptionsInterface[];
    tagsList: OptionsInterface[];
}

export interface SelectedFilterOptionsInterface {
    dbVendors: string[];
    tags: string[];
}

function Projects({ testId = 'Projects' }: Props): ReactElement {
    const history = useHistory();
    const { projectUuid, activeTab } = useParams<UrlProps>();
    const { refresh, invites } = useInvites();
    const [openPopover, setOpenPopover] = useState<boolean>(false);
    const [filterOptions, setFilterOptions] = useState<FilterOptionsInterface>({
        datastoreTypes: [],
        tagsList: [],
    });

    const [selectedFilterOptions, setSelectedFilterOptions] =
        useState<SelectedFilterOptionsInterface>({
            dbVendors: [],
            tags: [],
        });

    const [filterCount, setFilterCount] = useState<number>(0);

    const { datastores } = useAppSelector((state) => state.datastores);
    const { user } = useAppSelector((state) => state.user);
    const { status } = useCmonStatus();
    const dispatch = useAppDispatch();
    const { trackPageView } = usePiwikPro();

    useEffect(() => {
        trackPageView(LANDING_PAGE);
    }, []);

    useEffect(() => {
        dispatch(setInitialState());
    }, []);

    const limitReached = userReachedDatastoreLimit(datastores, user);
    useEffect(() => {
        if (datastores) {
            datastores.map((datastore: Deployment) =>
                dispatch(
                    clearIntervals({ dataStoreUuid: datastore.dataStoreUuid })
                )
            );
        }
    }, [datastores, dispatch]);

    useEffect(() => {
        if (datastores)
            setFilterOptions({
                tagsList: datastores.getTagsForFiltering(),
                datastoreTypes: datastores.getVendorsForFiltering(),
            });
    }, [datastores]);

    const onChangeDatastoreCheckbox = (value: string[]) => {
        setSelectedFilterOptions({
            ...selectedFilterOptions,
            dbVendors: value,
        });
        setFilterCount(value.length + selectedFilterOptions.tags.length);
    };

    const onChangeDatastoreTags = (value: string[]) => {
        setSelectedFilterOptions({
            ...selectedFilterOptions,
            tags: value,
        });
        setFilterCount(value.length + selectedFilterOptions.dbVendors.length);
    };

    const openFilter = () => {
        setOpenPopover(!openPopover);
    };

    const acceptInvite = async (invite: InviteInterface) => {
        try {
            await InviteMemberService.acceptInvite(invite.id);
            message.success('Invite accepted successfully');
            refresh();
            dispatch(userCheck());
        } catch (e: any) {
            console.error(e);
        }
    };

    const declineInvite = async (invite: InviteInterface) => {
        try {
            await InviteMemberService.declineInvite(invite.id);
            message.success('Invite declined successfully');
            refresh();
        } catch (e: any) {
            console.error(e);
        }
    };

    const operations =
        activeTab === 'data-stores' ? (
            <>
                <Popover
                    content={
                        <DatastoreFilterPopover
                            options={filterOptions}
                            onChangeDbTypesFilter={onChangeDatastoreCheckbox}
                            onChangeTagsFilter={onChangeDatastoreTags}
                        />
                    }
                    title={<strong>Filters</strong>}
                    trigger="click"
                    onOpenChange={() => openFilter()}
                    open={openPopover}
                    overlayInnerStyle={{
                        borderRadius: 16,
                    }}
                    overlayStyle={{
                        width: '250px',
                    }}
                >
                    <Badge
                        size="default"
                        className={styles.FilterBadge}
                        count={filterCount}
                    >
                        <Button className={styles.FilterButton}>
                            <FilterOutlined />
                        </Button>
                    </Badge>
                </Popover>
                <Can permission={CAN_ADD_DATABASE} showTooltip={false}>
                    <Tooltip title={limitReached ? user?.disabledText : ''}>
                        <Link to={`/projects/${projectUuid}/${activeTab}/add`}>
                            <Button
                                data-testid={`${testId}CreateNewButton`}
                                type="primary"
                                disabled={limitReached}
                                className={styles.ProjectsOperationButton}
                            >
                                {' '}
                                {'+'}
                                <span className={styles.IconTextMargin4}>
                                    Create{' '}
                                    {activeTab === 'data-stores'
                                        ? 'datastore'
                                        : 'VPC'}
                                </span>
                            </Button>
                        </Link>
                    </Tooltip>
                </Can>
            </>
        ) : activeTab === 'vpcs' ? (
            <Can permission={CAN_ADD_VPC} showTooltip={false}>
                <CreateVpcButton
                    buttonText="+ Create VPC"
                    buttonType="primary"
                    className={styles.ProjectsOperationButton}
                    size="middle"
                />
            </Can>
        ) : null;

    return (
        <section className={styles.Projects} data-testid={testId}>
            {status && !status.isFunctional() && (
                <Alert
                    className={styles.CmonStatusAlert}
                    showIcon
                    type="warning"
                    message={
                        <div className={styles.CmonStatusAlertMessage}>
                            Attention: Our system is currently undergoing
                            maintenance to enhance your experience. We
                            appreciate your patience as we work to restore full
                            functionality.
                        </div>
                    }
                />
            )}
            {invites && invites.invites.length > 0 && (
                <Alert
                    className={styles.CmonStatusAlert}
                    showIcon
                    type="info"
                    action={
                        <Row
                            gutter={16}
                            className={styles.ParameterGroupAlertAction}
                        >
                            <Col>
                                <Button
                                    onClick={() =>
                                        declineInvite(
                                            invites.getFirstPendingInvites()
                                        )
                                    }
                                >
                                    Decline{' '}
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    onClick={() =>
                                        acceptInvite(
                                            invites.getFirstPendingInvites()
                                        )
                                    }
                                >
                                    Accept
                                </Button>
                            </Col>
                        </Row>
                    }
                    message={`Pending Invitation ${
                        invites.total - 1 > 0 ? `(+${invites.total - 1})` : ''
                    }`}
                    description={
                        <div>
                            You have been invited to join{' '}
                            <strong>
                                {
                                    invites.getFirstPendingInvites()
                                        .organization.name
                                }
                                .
                            </strong>
                        </div>
                    }
                />
            )}

            <Tabs
                className={styles.ProjectsTabs}
                data-testid={`${testId}Tabs`}
                tabBarExtraContent={operations}
                type="card"
                activeKey={activeTab}
                onChange={(tabKey: any) => {
                    history.push(`/projects/${projectUuid}/${tabKey}`);
                }}
            >
                <Tabs.TabPane tab="Datastores" key="data-stores">
                    <Suspense fallback={<LazyLoader />}>
                        <DataStores
                            selectedFilters={selectedFilterOptions}
                            filterCount={
                                selectedFilterOptions.dbVendors.length +
                                selectedFilterOptions.tags.length
                            }
                        />
                    </Suspense>
                </Tabs.TabPane>

                {!vpcDisabled && (
                    <Tabs.TabPane tab="VPCs" key="vpcs">
                        <Suspense fallback={<LazyLoader />}>
                            <Vpcs />
                        </Suspense>
                    </Tabs.TabPane>
                )}

                <Tabs.TabPane tab="DB Parameters" key="db-parameters">
                    <Suspense fallback={<LazyLoader />}>
                        <DbParametersTable />
                    </Suspense>
                </Tabs.TabPane>
            </Tabs>
        </section>
    );
}

export default Projects;
